import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { WidgetProps } from '@rjsf/utils';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Autocomplete, Chip, CircularProgress, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';
import { debounce } from '@mui/material/utils';

import * as OrdersService from '~/services/OrdersService';

interface BookingOption {
  bookingNumber: string;
  orderId: string;
  label: string;
}

const ITEM_COLLECTIONS_WITH_BOOKING_NUMBER = [
  'items',
  'experience_items',
  'tour_items',
  'addon_items',
  'bedbank_items',
  'cruise_items',
] as const;

interface ValidatedBooking {
  bookingNumber: string;
  orderId: string;
}

function BookingNumberSearchWidget({ id, required, readonly, disabled, label, value, onChange }: WidgetProps) {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState<BookingOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [validatedBooking, setValidatedBooking] = useState<ValidatedBooking | null>(null);

  // Clear validated state if input changes away from validated value
  useEffect(() => {
    if (validatedBooking && inputValue !== validatedBooking.bookingNumber) {
      setValidatedBooking(null);
    }
  }, [inputValue, validatedBooking]);

  const searchBookings = useMemo(
    () =>
      debounce(async (searchValue: string) => {
        if (!searchValue || searchValue.length < 2) {
          setOptions([]);
          setHasSearched(false);
          return;
        }

        setIsLoading(true);
        setHasSearched(false);
        try {
          const result = await OrdersService.searchPurchases({ searchId: searchValue });

          const bookingOptions: BookingOption[] = [];

          if (result?.result) {
            for (const order of result.result) {
              const orderId = order.id_orders || order.id;
              for (const collection of ITEM_COLLECTIONS_WITH_BOOKING_NUMBER) {
                const items = order[collection];
                if (!Array.isArray(items)) {
                  continue;
                }
                for (const item of items) {
                  if (item.booking_number?.toLowerCase().includes(searchValue.toLowerCase())) {
                    bookingOptions.push({
                      bookingNumber: item.booking_number,
                      orderId,
                      label: `${item.booking_number} (Order: ${orderId})`,
                    });
                  }
                }
              }
            }
          }

          setOptions(bookingOptions);
          setHasSearched(true);
        } catch (error) {
          console.error('Error searching bookings:', error);
          setOptions([]);
          setHasSearched(true);
        } finally {
          setIsLoading(false);
        }
      }, 300),
    [],
  );

  // Cleanup debounced function on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      searchBookings.clear();
    };
  }, [searchBookings]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      setInputValue(newInputValue);
      if (reason === 'input') {
        searchBookings(newInputValue);
      }
    },
    [searchBookings],
  );

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: BookingOption | string | null) => {
      if (typeof newValue === 'object' && newValue !== null) {
        // Selected from dropdown - this is a validated booking
        onChange(newValue.bookingNumber);
        setInputValue(newValue.bookingNumber);
        setValidatedBooking({
          bookingNumber: newValue.bookingNumber,
          orderId: newValue.orderId,
        });
      } else if (typeof newValue === 'string') {
        // Manual entry - not validated
        onChange(newValue);
        setInputValue(newValue);
        setValidatedBooking(null);
      } else {
        onChange('');
        setInputValue('');
        setValidatedBooking(null);
      }
    },
    [onChange],
  );

  // Show warning when search completed but no results found
  const getNoOptionsText = () => {
    if (isLoading) {
      return 'Searching...';
    }
    if (hasSearched && options.length === 0 && inputValue.length >= 2) {
      return 'No bookings found - you can still enter a booking number manually';
    }
    return 'Type at least 2 characters to search';
  };

  const getHelperText = () => {
    if (validatedBooking) {
      return `Validated - Order: ${validatedBooking.orderId}`;
    }
    if (hasSearched && options.length === 0 && inputValue.length >= 2) {
      return 'No matching bookings found - entering manually';
    }
    return '';
  };

  return (
    <Stack spacing={1}>
      <Autocomplete
        id={id}
        freeSolo
        value={inputValue}
        inputValue={inputValue}
        disabled={disabled}
        readOnly={readonly}
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.bookingNumber)}
        isOptionEqualToValue={(option, val) =>
          typeof val === 'string' ? option.bookingNumber === val : option.bookingNumber === val.bookingNumber
        }
        filterOptions={(x) => x}
        loading={isLoading}
        loadingText="Searching bookings..."
        noOptionsText={getNoOptionsText()}
        onInputChange={handleInputChange}
        onChange={handleChange}
        renderOption={(props, option) => (
          <li {...props} key={option.bookingNumber}>
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            label={label || 'LE Booking Number'}
            placeholder="Search by booking number..."
            helperText={getHelperText()}
            color={validatedBooking ? 'success' : undefined}
            focused={validatedBooking ? true : undefined}
            InputProps={{
              ...params.InputProps,
              startAdornment: validatedBooking ? (
                <InputAdornment position="start">
                  <Tooltip title={`Validated - Order: ${validatedBooking.orderId}`}>
                    <CheckCircleIcon color="success" />
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {validatedBooking && (
        <Chip
          label={`${validatedBooking.bookingNumber} (Order: ${validatedBooking.orderId})`}
          color="success"
          size="small"
          onDelete={() => {
            setValidatedBooking(null);
            setInputValue('');
            onChange('');
          }}
        />
      )}
    </Stack>
  );
}

export default BookingNumberSearchWidget;
